/**
 * OrderStatusButtons
 *
 * Three pill-style buttons (Closed / Pending / Lost) with a required
 * Lost-reason modal. Colors + testids preserved so the Cart page's
 * quotation cards keep working exactly as before.
 *
 * Refactored to be endpoint-agnostic. Callers pass
 * ``onStatusChange(newStatus, reason)`` which does the actual API
 * call and returns a promise. This lets walk-ins and leads reuse the
 * same widget without importing the quotations endpoint. The
 * quotation site (CartPage / QuotationDetail / QuotationsPage) wraps
 * api.updateQuotationStatus in a small handler and passes it in.
 *
 * Props:
 *   • recordId (string) — used for testids only; the widget never
 *     hits the network by id directly anymore.
 *   • status, lostReason — current state (displayed).
 *   • onStatusChange(newStatus, reason) → Promise — REQUIRED.
 *     Return the promise so the widget can await it and toast on success.
 *   • onChange(newStatus, reason) — optional cache-refresh callback
 *     invoked after onStatusChange resolves.
 *   • size — 'sm' | 'md' visual density.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  closed: {
    label: 'Closed',
    activeClass: 'bg-emerald-700 text-white border-emerald-700',
    idleClass: 'border-emerald-700 text-emerald-700 hover:bg-emerald-50',
  },
  pending: {
    label: 'Pending',
    activeClass: 'bg-heda-ink text-white border-amber-700',
    idleClass: 'bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold',
  },
  lost: {
    label: 'Lost',
    activeClass: 'bg-red-700 text-white border-red-700',
    idleClass: 'border-red-700 text-red-700 hover:bg-red-50',
  },
};

export function OrderStatusButtons({ recordId, quotationId, status, lostReason, onStatusChange, onChange, size = 'sm' }) {
  // Backwards-compat: legacy callers still pass `quotationId`. Prefer
  // `recordId` for new callers so testids don't imply "this is a
  // quotation" when the widget is on a walk-in / lead row.
  const id = recordId ?? quotationId;
  const [pendingLost, setPendingLost] = useState(false);
  const [reason, setReason] = useState(lostReason || '');
  const [busy, setBusy] = useState(false);

  const current = (status || 'pending').toLowerCase();
  const isSm = size === 'sm';

  const apply = async (nextStatus, nextReason = '') => {
    if (typeof onStatusChange !== 'function') {
      toast.error('onStatusChange handler missing');
      return;
    }
    setBusy(true);
    try {
      await onStatusChange(nextStatus, nextReason);
      onChange?.(nextStatus, nextReason);
      toast.success(`Marked as ${STATUS_CONFIG[nextStatus].label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const onPick = (s) => {
    if (s === current) return; // already active
    if (s === 'lost') {
      setReason(lostReason || '');
      setPendingLost(true);
      return;
    }
    apply(s);
  };

  const submitLost = () => {
    const r = reason.trim();
    if (!r) {
      toast.error('Please tell us why the order was lost');
      return;
    }
    setPendingLost(false);
    apply('lost', r);
  };

  return (
    <>
      {/* Compact inline segmented control — replaces the old three-pill row.
          On small screens the control spans full card width with three
          equal segments (grid-cols-3, explicit h-9 with items-center so
          text sits vertically centered inside the pill — otherwise the
          descenders of "Pending" / "Lost" clipped at the bottom edge of
          the rounded container on 390 px cards); on >=sm it collapses
          back to a natural-width inline pill so it sits next to
          amount/actions. `whitespace-nowrap` + `leading-none` keep the
          labels on a single tight line. */}
      <div
        role="radiogroup"
        aria-label="Order status"
        className={`grid grid-cols-3 sm:inline-flex sm:items-stretch w-full sm:w-auto rounded-full border border-heda-line bg-white overflow-hidden h-9 ${isSm ? 'sm:h-7' : 'sm:h-8'}`}
        data-testid={`status-row-${id}`}
      >
        {(['closed', 'pending', 'lost']).map((s, i) => {
          const cfg = STATUS_CONFIG[s];
          const isActive = current === s;
          return (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); onPick(s); }}
              className={`flex items-center justify-center leading-none px-2 sm:px-3 text-[11px] sm:text-xs font-medium whitespace-nowrap text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                i > 0 ? 'border-l border-heda-line' : ''
              } ${
                isActive
                  ? cfg.activeClass  // preserved from STATUS_CONFIG exactly
                  : 'text-heda-sub hover:bg-heda-cream'
              }`}
              data-testid={`status-btn-${s}-${id}`}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
      {current === 'lost' && lostReason && (
        <p
          className="text-[10px] text-red-700 italic mt-1.5 line-clamp-2 break-words"
          title={lostReason}
          data-testid={`status-lost-reason-${id}`}
        >
          Reason: {lostReason}
        </p>
      )}

      <Dialog open={pendingLost} onOpenChange={setPendingLost}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Why was this order lost?</DialogTitle>
            <DialogDescription className="text-xs text-heda-sub mt-1">
              Required. The admin uses this to audit churn — e.g. <em>"price too high"</em>, <em>"chose competitor"</em>, <em>"customer postponed"</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="lost-reason-input" className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Reason</Label>
            <Textarea
              id="lost-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Type the reason here…"
              rows={3}
              className="rounded-xl bg-heda-cream/50 resize-none"
              data-testid="lost-reason-input"
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPendingLost(false)} className="rounded-xl sm:flex-1 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold" data-testid="lost-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitLost} disabled={busy} className="rounded-xl sm:flex-1 bg-red-700 hover:bg-red-800 text-white" data-testid="lost-submit-btn">
              Mark as Lost
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OrderStatusButtons;
